import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';

import { ReportError } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import CartSummary from '@/components/_cart/CartSummary.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import { CardLayout } from '@/components/layouts/index.tsx';
import LoadingOverlay from '@/components/loading/LoadingOverlay.tsx';
import PaymentForm from '@/components/payment/PaymentForm.tsx';
import useCartPaymentOptions from '@/lib/queries/useCartPaymentOptions.ts';
import useCart from '@/lib/useCart.tsx';

const Checkout = () => {
  const { cart, clearCart, checkoutCreate, checkoutDelete, refreshCart } = useCart();
  const { t } = useTranslation(['common', 'register', 'checkout']);
  const navigate = useNavigate();

  const paymentOptions = useCartPaymentOptions();

  const doCheckout = async (method: 'stripe' | 'paypal' | 'none') => {
    if (paymentOptions.hasPendingOrder && paymentOptions.checkoutUrl) {
      window.location.href = paymentOptions.checkoutUrl;
      return;
    }

    const res = await checkoutCreate.mutateAsync(method);
    switch (res.status) {
      case 201: {
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        } else if (res.data.payment_method === 'none') {
          void navigate({ to: '/checkout/success', search: { orderId: cart.data?.id } });
        }
        return;
      }
      default: {
        throw new ReportError(res);
      }
    }
  };

  const handleCancelPayment = async () => {
    await checkoutDelete.mutateAsync();
    await paymentOptions.refetch();
  };

  const isLoading =
    cart.isPending ||
    paymentOptions.isLoading ||
    checkoutDelete.isPending ||
    checkoutCreate.isPending;

  const hasItems = Boolean(cart.data?.items.length);
  const showPaymentForm = hasItems && !paymentOptions.isLoading;

  const handleCancelOrder = async () => {
    await clearCart.mutateAsync();
    refreshCart();
    void navigate({ to: '/profile' });
  };

  return (
    <div>
      <h1 className="text-center my-2 my-lg-4 pb-3">{t('shared.navigation.checkout')}</h1>
      <CartSummary
        isLoading={cart.isPending}
        allowRemoval={cart.data?.status === 'OPEN'}
        items={cart.data?.items}
        total={cart.data?.total}
      />
      {hasItems && (
        <div className="d-flex justify-content-center mb-4">
          <span>
            <Trans
              ns="register"
              i18nKey="payment.no_refunds"
              components={{
                a: <a href="https://furality.org/codeofconduct" rel="noreferrer" target="_blank" />,
              }}
            />
          </span>
        </div>
      )}
      <div className="position-relative">
        {(checkoutCreate.isPending || checkoutDelete.isPending) && <LoadingOverlay />}
        {hasItems && cart.data?.total !== 0 && (
          <div className="d-flex justify-content-center mb-4">
            <span className="h5">
              {paymentOptions.hasPendingOrder
                ? t('register:payment.continue_to_payment')
                : t('checkout:payment.choose_payment_method')}
            </span>
          </div>
        )}

        {showPaymentForm && (
          <PaymentForm
            disabled={isLoading}
            onSelect={doCheckout}
            onCancel={handleCancelPayment}
            showCancelButton={paymentOptions.hasPendingOrder}
            methods={paymentOptions.methods}
          />
        )}
        {hasItems && !paymentOptions.hasPendingOrder && (
          <Button
            onClick={handleCancelOrder}
            size="sm"
            variant="outline-danger"
            className="d-block mx-auto mt-4"
          >
            {t('checkout:payment.cancel_order.button')}
          </Button>
        )}
        {!hasItems && (
          <div className="d-flex justify-content-center mb-4">
            <LinkButton to="/profile" className="mt-4">
              {t('shared.return_to_profile')}
            </LinkButton>
          </div>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/checkout/')({
  component: () => (
    <CardLayout title="shared.navigation.checkout">
      <Checkout />
    </CardLayout>
  ),
});
